import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, map, throwError } from 'rxjs';

import { environment } from '../../../environments/environment';
import { DiagnosticEnvelope } from './diagnostic.interfaces';
import { DiagnosticSessionService } from './diagnostic-session.service';

const DIAGNOSTIC_SESSION_HEADER = 'x-diagnostic-session-id';
const REQUEST_ID_HEADER = 'x-request-id';

export const diagnosticInterceptor: HttpInterceptorFn = (request, next): Observable<HttpEvent<unknown>> => {
  const diagnostics = inject(DiagnosticSessionService);
  const sessionId = diagnostics.getSessionId();

  if (!sessionId || !isLogicApiRequest(request.url)) return next(request);

  const requestId = diagnostics.createRequestId();
  const startedAt = Date.now();
  const diagnosticRequest = request.clone({
    setHeaders: {
      [DIAGNOSTIC_SESSION_HEADER]: sessionId,
      [REQUEST_ID_HEADER]: requestId,
    },
  });

  return next(diagnosticRequest).pipe(
    map(event => {
      if (!(event instanceof HttpResponse)) return event;

      if (!isDiagnosticEnvelope(event.body)) {
        diagnostics.captureFrontendOutcome({
          requestId,
          method: request.method,
          startedAt,
          statusCode: event.status,
          responseType: request.responseType,
          outcome:
            request.responseType === 'blob' || request.responseType === 'arraybuffer'
              ? 'BINARY_OR_UNWRAPPED_RESPONSE'
              : 'DIAGNOSTIC_ENVELOPE_MISSING',
          contentType: event.headers.get('content-type'),
        });
        return event;
      }

      diagnostics.capture(event.body.__diagnostic);
      return event.clone({ body: event.body.data });
    }),
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse) || !isDiagnosticEnvelope(error.error)) {
        if (error instanceof HttpErrorResponse) {
          const supportId = error.headers.get(REQUEST_ID_HEADER) ?? requestId;
          diagnostics.registerErrorSupportId(supportId, requestId);
          diagnostics.captureFrontendOutcome({
            requestId: supportId,
            method: request.method,
            startedAt,
            statusCode: error.status,
            responseType: request.responseType,
            outcome: 'HTTP_ERROR_WITHOUT_ENVELOPE',
            contentType: error.headers.get('content-type'),
          });
        }
        return throwError(() => error);
      }

      const envelope = error.error;
      diagnostics.registerErrorSupportId(readSupportId(envelope.data), envelope.__diagnostic.requestId);
      diagnostics.capture(envelope.__diagnostic);

      return throwError(
        () =>
          new HttpErrorResponse({
            error: envelope.data,
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url ?? undefined,
          }),
      );
    }),
  );
};

function isLogicApiRequest(url: string): boolean {
  const apiBaseUrl = environment.baseURL.replace(/\/$/, '');
  return url === apiBaseUrl || url.startsWith(`${apiBaseUrl}/`) || url.startsWith(`${apiBaseUrl}?`);
}

function isDiagnosticEnvelope(value: unknown): value is DiagnosticEnvelope<unknown> {
  if (!isRecord(value) || !Object.prototype.hasOwnProperty.call(value, 'data')) return false;

  const diagnostic = value['__diagnostic'];
  return (
    isRecord(diagnostic) &&
    typeof diagnostic['diagnosticSessionId'] === 'string' &&
    typeof diagnostic['requestId'] === 'string' &&
    Array.isArray(diagnostic['events'])
  );
}

function readSupportId(value: unknown): unknown {
  return isRecord(value) ? value['supportId'] : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
